/*
 * formatQuarterName: This function returns the text label of a quarter based on it's numerical value
 *
 * @param {int} quarter - integer corresponding to a quarter
 * @return {string} quarter - text representing the period in plain english
 */
export const formatQuarterName = quarter => {
    switch (quarter) {
        case 1:
            return '1st'
        case 2:
            return '2nd'
        case 3:
            return '3rd'
        case 4:
            return '4th'
        case 5:
            return 'OT'
        default:
            return quarter - 4 + 'OT'
    }
}

export function formatLastNineEvents(lastNineEvents = [], homeTeam, visitingTeam) {
    const ghostPlays = [
        { id: 1, isGhost: true },
        { id: 2, isGhost: true },
        { id: 3, isGhost: true },
        { id: 4, isGhost: true },
        { id: 5, isGhost: true },
        { id: 6, isGhost: true },
        { id: 7, isGhost: true },
        { id: 8, isGhost: true },
        { id: 9, isGhost: true },
    ]

    if (!lastNineEvents) return ghostPlays

    const massagedPlays = lastNineEvents.map(play => {
        const formattedPlayDescription = play.description
        const teamLogo =
            play.team_id === homeTeam.id ? homeTeam.image_url_90 : visitingTeam.image_url_90

        return {
            id: play.id,
            title: setPlayTitle(play),
            color: getPlayColor(play.event_id),
            bodyText: shortenPlayerNames(
                formattedPlayDescription,
                play.player_display_name,
                play.position
            ),
            teamLogo: teamLogo,
            showPlayerHeadshot: false,
        }
    })

    return massagedPlays.concat(ghostPlays.slice(massagedPlays.length))
}

function setPlayTitle(play) {
    /* insert logic to rename events if needed */
    let formattedEvent = play
    let formattedQuarter = formatQuarterName(play.quarter)

    switch (play.event_id) {
        case 1:
        case 2:
            formattedEvent = 'FT'
            break
        case 3:
        case 4:
            formattedEvent = 'FG'
            break
        case 5:
            formattedEvent = 'OREB'
            break
        case 6:
            formattedEvent = 'DREB'
            break
        case 7:
            formattedEvent = 'TO'
            break
        case 8:
            formattedEvent = 'FOUL'
            break
        case 9:
            formattedEvent = 'VIOL'
            break
        case 10:
            play.time !== '10:00' ? (formattedEvent = 'SUB') : (formattedEvent = 'STARTER')
            break
        case 11:
            formattedEvent = 'TIMEOUT'
            break
        case 12:
            formattedEvent = 'JUMP'
            break
        case 13:
            formattedEvent = 'EJECT'
            break
        case 14:
        case 15:
            formattedEvent = 'REPLAY'
            break
        default:
            formattedEvent = play.event
    }

    formattedEvent =
        formattedEvent !== 'STARTER'
            ? `${formattedEvent} - ${play.time} / ${formattedQuarter}`
            : `${formattedEvent}`

    return formattedEvent || ''
}

function shortenPlayerNames(originalText, playerDisplayName, playerPosition) {
    let newText = originalText

    if (newText === 'Starting Lineup') {
        newText = `${playerDisplayName} - ${playerPosition}`
    }

    return newText
}

function getPlayColor(playTypeId) {
    const darkBlue = 'darkBlue'
    const red = 'red'
    const grey = 'grey'
    const brightBlue = 'brightBlue'

    const playColorMap = new Map([
        [1, darkBlue],
        [2, darkBlue],
        [3, darkBlue],
        [4, darkBlue],
        [5, brightBlue],
        [6, brightBlue],
        [7, brightBlue],
        [8, red],
        [9, red],
        [10, red],
        [11, red],
        [12, grey],
        [13, red],
        [14, brightBlue],
        [15, grey],
    ])

    return playColorMap.get(playTypeId) || grey
}

export function formatSeasonLeaders(homeTeam, visitingTeam) {
    const leaderCategories = [
        {
            title: 'Points',
            categoryKey: 'points_per_game_leader',
            valueKey: 'total',
            decimalPlaces: 1,
        },
        {
            title: 'Assists',
            categoryKey: 'assists_per_game_leader',
            valueKey: 'total',
            decimalPlaces: 1,
        },
        {
            title: 'Rebounds',
            categoryKey: 'rebounds_per_game_leader',
            valueKey: 'total',
            decimalPlaces: 1,
        },
        {
            title: 'FG%',
            categoryKey: 'field_goal_percentage_leader',
            valueKey: 'total',
            isPercentage: true,
        },
    ]

    const seasonLeaders = leaderCategories.map(leaderCategory => {
        const homeTeamData = homeTeam?.season_leaders?.[leaderCategory.categoryKey]
        const visitingTeamData = visitingTeam?.season_leaders?.[leaderCategory.categoryKey]

        if (!homeTeamData || !visitingTeamData) {
            return null
        }

        let homeTeamValue = homeTeamData[leaderCategory.valueKey]
        let visitingTeamValue = visitingTeamData[leaderCategory.valueKey]

        if (leaderCategory.isPercentage) {
            homeTeamValue = (homeTeamValue * 100).toFixed(1)
            visitingTeamValue = (visitingTeamValue * 100).toFixed(1)
        }

        if (leaderCategory.decimalPlaces) {
            homeTeamValue = homeTeamValue.toFixed(leaderCategory.decimalPlaces)
            visitingTeamValue = visitingTeamValue.toFixed(leaderCategory.decimalPlaces)
        }

        return {
            title: leaderCategory.title,
            homeTeamShortName: homeTeam.short_name,
            homeTeamColor: homeTeam.color,
            visitingTeamColor: visitingTeam.color,
            visitingTeamShortName: visitingTeam.short_name,
            homeTeamLogo: homeTeam.image_url_90,
            visitingTeamLogo: visitingTeam.image_url_90,
            homeTeamValue: homeTeamValue,
            visitingTeamValue: visitingTeamValue,
            homeTeamPlayerName: `${homeTeamData.first_name.charAt(0)}. ${homeTeamData.last_name}`,
            visitingTeamPlayerName: `${visitingTeamData.first_name.charAt(0)}. ${
                visitingTeamData.last_name
            }`,
            homeTeamDetails: {
                first_name: homeTeamData.first_name,
                last_name: homeTeamData.last_name,
                id: homeTeamData.id,
            },
            visitingTeamDetails: {
                first_name: visitingTeamData.first_name,
                last_name: visitingTeamData.last_name,
                id: visitingTeamData.id,
            },
        }
    })
    return seasonLeaders.filter(n => n) // filter out null values
}

export const wnbaTeamNameIDArrayMap = new Map([
    [149, 'los-angeles-sparks'],
    [150, 'new-york-liberty'],
    [151, 'phoenix-mercury'],
    [153, 'las-vegas-aces'],
    [154, 'washington-mystics'],
    [155, 'dallas-wings'],
    [156, 'minnesota-lynx'],
    [157, 'connecticut-sun'],
    [158, 'indiana-fever'],
    [161, 'seattle-storm'],
    [5691, 'chicago-sky'],
    [8406, 'atlanta-dream'],
])

export function isGameTypePostseason(gameType) {
    switch (gameType) {
        case 'Post Season':
            return true
        default:
            return false
    }
}

export function isTeamLogoVisible(teamShortName) {
    const hiddenLogos = ['COL', 'CLK']
    return !hiddenLogos.includes(teamShortName)
}
